import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Outlet, useLocation } from 'react-router-dom';
import { useSearchParams } from "react-router-dom";
import { Box, Paper } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';

import DashboardNavbar from './navbar';
import DashboardSidebar from './sidebar';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
import { AuthService } from '../../services/auth.service';
import { AlertListener } from '../../components/play-sound-alert-listener';
import { requestFbPushForToken } from '../../utils/firebase';
import { AlertTypes } from '../../types';
import { AlertService } from '../../services/alert.service';
import { AlertDetailsDrawer } from '../../components/details-drawers/alert-details-drawer';
import { SessionExpiredModal } from '../../components/session-expired-modal/session-expired-modal';
import { PermissionsChangedListener } from '../../components/permissions-changed-listener';
import { isRequestCanceled } from '../../http';
import { BannerDisplay } from '../../components/banner-display';
import { useEffectiveSettings, SETTING } from '../../hooks/use-effective-settings';
import { Loading } from '../../components';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { needsTermsAcceptance, TermsAcceptanceGate } from '../../components/terms-acceptance-gate';
import { AiChatWidget } from '../../components/ai-chat';

export const HEADER = {
    MOBILE_HEIGHT: 64,
    MAIN_DESKTOP_HEIGHT: 88,
    DASHBOARD_DESKTOP_HEIGHT: '10vh',
    DASHBOARD_DESKTOP_OFFSET_HEIGHT: 92 - 32,
};

export const NAVBAR = {
    BASE_WIDTH: 260,
    DASHBOARD_WIDTH: 280,
    DASHBOARD_COLLAPSE_WIDTH: 88,
    //
    DASHBOARD_ITEM_ROOT_HEIGHT: 48,
    DASHBOARD_ITEM_SUB_HEIGHT: 40,
    DASHBOARD_ITEM_HORIZONTAL_HEIGHT: 32,
};

export const ICON = {
    NAVBAR_ITEM: 22,
    NAVBAR_ITEM_HORIZONTAL: 20,
};

const MainStyle = styled('main', {
    shouldForwardProp: (prop) => prop !== 'collapseClick',
})(({ theme }) => ({
    flexGrow: 1,
    paddingTop: HEADER.MOBILE_HEIGHT + 24,
    paddingBottom: HEADER.MOBILE_HEIGHT + 24,
    [theme.breakpoints.up('lg')]: {
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: `calc(${HEADER.DASHBOARD_DESKTOP_HEIGHT} + 24px)`,
        paddingBottom: `calc(${HEADER.DASHBOARD_DESKTOP_HEIGHT} + 24px)`,
        width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
        transition: theme.transitions.create('margin-left', {
            duration: theme.transitions.duration.shorter,
        }),
    },
}));

interface Props {}

const DashboardLayout: React.FC<Props> = () => {
    const { collapseClick } = useCollapseDrawer();
    const { isLoading: settingsLoading, getSetting } = useEffectiveSettings();
    const { authUser } = useSelector((state: RootState) => state.auth);
    const [open, setOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const [alertDetails, setAlertDetails] = useState<AlertTypes.Model | null>(null);

    const alertDetailsSearchId = searchParams.get('alertDetails');

    useEffect(() => {
        let ignore = false;
        const controller = new AbortController();

        (async () => {
            try {
                const pushToken = await requestFbPushForToken();
                if (ignore) {
                    return;
                }

                if (pushToken) {
                    await AuthService.setPushToken(pushToken);
                }

                await AuthService.whoIam(controller.signal);
            } catch (error) {
                if (isRequestCanceled(error)) {
                    return;
                }

                console.error('Dashboard bootstrap failed (push token / whoIam):', error);
            }
        })();

        return () => {
            ignore = true;
            controller.abort();
        };
    }, []);

    useEffect(() => {
        let ignore = false;
        const controller = new AbortController();

        (async () => {
            if (!alertDetailsSearchId) {
                setAlertDetails(null);
                return;
            }

            try {
                const alert = await AlertService.getAlertDetails(alertDetailsSearchId, controller.signal);

                if (!ignore) {
                    setAlertDetails(alert);
                }
            } catch (error) {
                if (isRequestCanceled(error)) {
                    return;
                }

                console.error('Failed to load alert details from query param:', error);
                if (!ignore) {
                    setAlertDetails(null);
                }
            }
        })();

        return () => {
            ignore = true;
            controller.abort();
        };
    }, [alertDetailsSearchId]);

    // Settings-before-render: the nav, invite code, and default route all key
    // off customer settings, and rendering them against catalog defaults
    // flashes the full platform at orgs that don't have it (worst case: an
    // IRA-standalone trial user seeing every Safety Hub tab for a beat).
    // isLoading is true only on the initial uncached fetch per session scope;
    // background refetches never re-trigger this gate, and a failed fetch
    // falls through and renders with defaults rather than blocking the app.
    if (settingsLoading) {
        // Keep the 401 listener mounted while the shell is held: if the very
        // first /v2/settings/me answers 401 the session is dead and the user
        // must be logged out right away, not once some later request fails.
        return (
            <>
                <Loading />
                <SessionExpiredModal />
            </>
        );
    }

    // Terms-before-anything for standalone trial orgs: the gate replaces the
    // whole shell (no nav behind it) until this user has accepted the current
    // TERMS_VERSION. Placed after the settings gate so organization.type is
    // resolved, and skipped for masquerading POM staff (acceptance is the
    // customer's legal act; the API rejects masquerade writes anyway).
    const isIraStandalone = getSetting<string>(SETTING.organizationType, 'full') === 'iraStandalone';
    if (needsTermsAcceptance(authUser, isIraStandalone)) {
        // Same reason as above: a token can die while the user sits on the
        // terms gate, and its accept call must log them out, not stall.
        return (
            <>
                <TermsAcceptanceGate />
                <SessionExpiredModal />
            </>
        );
    }

    return (
        <>
            <Box
                sx={{
                    display: { lg: 'flex' },
                    minHeight: { lg: 1 },
                }}
            >
                <DashboardNavbar onOpenSidebar={() => setOpen(true)} />
                <DashboardSidebar
                    isOpenSidebar={open}
                    onCloseSidebar={() => setOpen(false)}
                />
                <MainStyle
                    sx={{
                        ...(collapseClick && {
                            marginLeft: '102px',
                        }),
                        '&::-webkit-scrollbar':{
                            display: 'none',
                            width: 0,
                            height: 0
                        }
                    }}
                >
                    <Paper>
                        <AlertListener />
                    </Paper>
                    <BannerDisplay />
                    <Outlet />
                </MainStyle>
            </Box>

            {alertDetails && 
                <AlertDetailsDrawer 
                    visible={!!alertDetails}
                    onClose={() => setAlertDetails(null)}
                    alert={alertDetails}
                    type={alertDetails?.alertType}

                />
            }

            {/* Session Expired Modal for 401 Errors */}
            <SessionExpiredModal />

            {/* Permissions Changed Listener for 403 Errors */}
            <PermissionsChangedListener />

            {/* Internal prototype: AI support chat button (bottom-right). Renders
                nothing unless REACT_APP_AI_CHAT_ENABLED is on — see
                components/ai-chat/ai-chat-flag.ts. */}
            <AiChatWidget />
        </>
    );
};

export default DashboardLayout;
